import React, { useState, useEffect, useCallback, useRef  } from 'react';
import { getData } from '../../../services/admin/revenueForms/razorpayTransactionsServices';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';

const RazorpayTransactions = () => {
  const [dataList, setDataList] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimeoutRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [subDomainFilter, setSubDomainFilter] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Debounce Effect
  useEffect(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
      setCurrentPage(1); // reset page only when actual search triggered
    }, 600); // 500ms debounce delay

    return () => clearTimeout(debounceTimeoutRef.current);
  }, [searchText]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (subDomainFilter) filters.domain = subDomainFilter;

      const params = {
        page: currentPage,
        limit: rowsPerPage,
        search: debouncedSearch,
        filters,
        startDate: startDate || null,
        endDate: endDate || null,
      };

     const response = await getData(params);
     // ✅ Console log the full API response
      console.log('Fetched Data:', response);
      setDataList(response.rows);
      setTotalRows(response.count);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, debouncedSearch, subDomainFilter, startDate, endDate]);

  

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(page);
  };

  const columns = [
    {
      name: '#',
      selector: (row, index) => (currentPage - 1) * rowsPerPage + (index + 1),
      sortable: false,
      width: '60px',
    },
    {
      name: 'Order Id',
      selector: (row) => row.order_id,
      sortable: true,
    },
    {
      name: 'Razorpay Order Id',
      selector: (row) => row.razorpay_order_id,
      sortable: true,
    },
    {
      name: 'Sub Domain',
      selector: (row) => row.sub_domain,
      sortable: true,
    }, 
    {
      name: 'Currency',
      selector: (row) => row.currency,
      sortable: true,
    },
    {
      name: 'Amount',
      selector: (row) => row.amount,
      sortable: true,
    },
    {
      name: 'Status',
      cell: (row) => {
        const status = row.status;
        let statusClass = '';
        let statusText = status;

        if (status === 'Success') statusClass = 'bg-success text-white px-2 py-1 rounded';
        else if (status === 'Cancelled') statusClass = 'bg-warning text-white text-dark px-2 py-1 rounded';
        else if (status === 'Failed') statusClass = 'bg-danger text-white px-2 py-1 rounded';
        else if (status === 'None') statusClass = 'bg-secondary text-white px-2 py-1 rounded';
        else if (status === 'Pending') statusClass = 'bg-secondary text-white px-2 py-1 rounded';

        return <div className={`status-btn ${statusClass}`}>{statusText}</div>;
      },
      sortable: true,
    },
  ];

  // Function to render expandable row details
  const ExpandableRow = ({ data }) => {
    return (
      <div className="p-3 bg-light border">
        <table className="table table-bordered">
          <tbody>
            <tr>
              <td><b>Signature:</b></td>
              <td>{data.signature }</td>
            </tr> 
            <tr>
              <td><b>Payment ID:</b></td>
              <td>{data.payment_id}</td>
            </tr>
            
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />

        <div className="row m-3 d-flex flex-column gap-3">
          <div className="d-flex gap-2">
            <div className="col-md-2">
              <span>Sub Domain</span>
              <select className="form-control" value={subDomainFilter} onChange={(e) => { setSubDomainFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">All Subdomains</option>
                {Array.from(new Set(dataList.map(item => item.domain))).map((subDomain, idx) => (
                  <option key={idx} value={subDomain}>{subDomain}</option>
                ))}
              </select>
            </div>
           
            <div className="col-md-2">
              <span>Start Date</span>
              <input type="date" className="form-control" value={startDate} onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }} />
            </div>
            <div className="col-md-2">
              <span>End Date</span>
              <input type="date" className="form-control" value={endDate} onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }} />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8">
                  <p className="fs-4">Conference Leads / Revenue Forms / Razorpay Transactions</p>
                </div>
              </div>
              <DataTable
                columns={columns}
                data={dataList}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={rowsPerPage}
                onChangePage={handlePageChange}
                paginationDefaultPage={currentPage}
                onChangeRowsPerPage={handlePerRowsChange}
                highlightOnHover
                responsive
                striped
                subHeader
                subHeaderComponent={
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Search"
                    value={searchText}
                    onChange={handleSearchChange}
                  />
                }
                expandableRows
                expandableRowsComponent={ExpandableRow}  // ✅ Adding expandable row
            />
             
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default RazorpayTransactions;
